<template>
  <div class="preview-root" :class="{ 'preview-root--fill': fillHeight }">
    <div class="d-flex justify-space-between align-center ga-4 pb-3">
      <slot name="headerLeft">
        <div data-testid="preview-title">{{ title }}</div>
      </slot>
      <div class="d-flex align-center ga-3">
        <slot name="toggleStart"></slot>
        <SoButtonGroup
          v-model="viewport"
          :items="viewports"
          data-testid="preview-viewport-toggle"
        />
      </div>
    </div>
    <div
      class="preview-wrapper d-flex justify-center"
      :class="{ 'preview-wrapper--flush': viewport === HTML_SOURCE }"
    >
      <pre
        v-if="viewport === PLAIN_TEXT"
        class="preview-plain"
        data-testid="email-template-plain-preview"
        >{{ plainContent }}</pre>
      <SoCodeBlock
        v-else-if="viewport === HTML_SOURCE"
        class="preview-source"
        :code="htmlContent"
        data-test-id="email-template-html-source"
      />
      <div
        v-else-if="!renderedHtml"
        class="preview-placeholder"
        data-testid="email-template-preview-empty"
      >
        <div class="font-weight-medium">HTML Preview</div>
        <div class="text-medium-emphasis">No content to render</div>
      </div>
      <iframe
        v-else
        :srcdoc="renderedHtml"
        :style="frameStyle"
        class="preview-frame"
        sandbox=""
        title="Email template preview"
        data-testid="email-template-preview"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { debounce, type DebouncedFunc } from "lodash-es"
  import SoButtonGroup from "@/components/SoButtonGroup.vue"
  import SoCodeBlock from "@/components/SoCodeBlock.vue"

  const PLAIN_TEXT = "Plain Text"
  const HTML_SOURCE = "HTML"
  const PREVIEW_DEBOUNCE_MS = 400

  const VIEWPORT_WIDTHS: Record<string, string> = {
    Mobile: "375px",
    Desktop: "100%",
  }

  export default defineComponent({
    components: { SoButtonGroup, SoCodeBlock },
    props: {
      htmlContent: {
        type: String,
        default: "",
      },
      plainContent: {
        type: String,
        default: "",
      },
      title: {
        type: String,
        default: "",
      },
      fillHeight: {
        type: Boolean,
        default: false,
      },
      // External templates carry a single body, with no plain-text counterpart.
      hidePlainText: {
        type: Boolean,
        default: false,
      },
      // Redundant where the source is already on screen, as in the edit form.
      hideHtmlSource: {
        type: Boolean,
        default: false,
      },
      // Debounce applies to typing (create form). Pass 0 where htmlContent
      // changes discretely (version switches) so the preview updates at once.
      renderDebounceMs: {
        type: Number,
        default: PREVIEW_DEBOUNCE_MS,
      },
    },
    data() {
      return {
        PLAIN_TEXT,
        HTML_SOURCE,
        viewport: "Desktop",
        // Trails htmlContent so the iframe re-renders only once typing settles.
        renderedHtml: this.htmlContent,
        updateRenderedHtml: null as DebouncedFunc<() => void> | null,
      }
    },
    computed: {
      viewports(): string[] {
        return [
          ...Object.keys(VIEWPORT_WIDTHS),
          ...(this.hideHtmlSource ? [] : [HTML_SOURCE]),
          ...(this.hidePlainText ? [] : [PLAIN_TEXT]),
        ]
      },
      frameStyle(): Record<string, string> {
        return {
          width: VIEWPORT_WIDTHS[this.viewport],
          maxWidth: "100%",
        }
      },
    },
    watch: {
      htmlContent() {
        if (this.updateRenderedHtml) this.updateRenderedHtml()
        else this.renderedHtml = this.htmlContent
      },
    },
    created() {
      if (this.renderDebounceMs > 0) {
        this.updateRenderedHtml = debounce(() => {
          this.renderedHtml = this.htmlContent
        }, this.renderDebounceMs)
      }
    },
    beforeUnmount() {
      this.updateRenderedHtml?.cancel()
    },
  })
</script>

<style scoped>
  .preview-wrapper {
    background: rgb(var(--v-theme-surface-dim));
    border: 1px solid rgb(var(--v-theme-border));
    border-radius: var(--theme-radius);
    padding: 16px;
    overflow: auto;
  }
  .preview-frame {
    height: 600px;
    border: none;
    background: #fff;
    transition: width 200ms ease;
  }
  .preview-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 4px;
    width: 100%;
    min-height: 600px;
  }
  .preview-root--fill .preview-placeholder {
    min-height: 0;
    flex: 1;
  }
  .preview-plain {
    width: 100%;
    margin: 0;
    padding: 16px;
    background: #fff;
    border-radius: var(--theme-radius);
    white-space: pre-wrap;
    overflow-wrap: break-word;
    font-family: inherit;
    overflow: auto;
  }
  /* The code block brings its own padding; the wrapper's would double it. */
  .preview-wrapper--flush {
    padding: 0;
  }
  .preview-source {
    width: 100%;
  }
  .preview-root--fill .preview-source {
    height: 100%;
  }
  .preview-root--fill .preview-plain {
    height: 100%;
  }
  .preview-root--fill {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .preview-root--fill .preview-wrapper {
    flex: 1;
  }
  .preview-root--fill .preview-frame {
    height: 100%;
  }
</style>
